import React, { useEffect, useState } from 'react';
import {
  Box, VStack, HStack, Flex, Heading, Text, Button, SimpleGrid, Card, CardHeader,
  CardBody, Icon, Alert, AlertIcon, FormControl, FormLabel, Input, Select,
  Spinner, useToast, Divider, Badge,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { MdHowToReg, MdSportsSoccer } from 'react-icons/md';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../../api/AxiosInstance';

const initialForm = {
  nombre: '', apPaterno: '', apMaterno: '', fechaNacimiento: '', sexo: '',
  curp: '', telefono: '', email: '',
  localidad: '', colonia: '', vialidad: '', numeroExterior: '', referencias: '',
};

const InscripcionDeportesPublico: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const preConvId = searchParams.get('convocatoriaId') || '';

  const [convocatorias, setConvocatorias] = useState<any[]>([]);
  const [loadingConv, setLoadingConv] = useState(true);
  const [convocatoriaId, setConvocatoriaId] = useState<string>(preConvId);
  const [form, setForm] = useState({ ...initialForm });
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      setLoadingConv(true);
      try {
        const { data } = await axiosInstance.get('/deportes/convocatorias', {
          params: { publicadaPortal: true },
        });
        setConvocatorias(Array.isArray(data) ? data : (data?.data || []));
      } catch {
        setConvocatorias([]);
      } finally {
        setLoadingConv(false);
      }
    })();
  }, []);

  const setField = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const validar = (): string | null => {
    if (!convocatoriaId) return 'Seleccione la convocatoria a la que desea inscribirse.';
    if (!form.nombre.trim()) return 'El nombre es obligatorio.';
    if (!form.apPaterno.trim()) return 'El apellido paterno es obligatorio.';
    if (!form.fechaNacimiento) return 'La fecha de nacimiento es obligatoria.';
    if (form.curp && form.curp.trim().length !== 18) return 'La CURP debe tener 18 caracteres.';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'El correo electrónico no es válido.';
    if (form.telefono && !/^\d{7,15}$/.test(form.telefono.replace(/\D/g, ''))) return 'El teléfono no es válido.';
    return null;
  };

  const handleSubmit = async () => {
    const err = validar();
    if (err) {
      setError(err);
      toast({ title: err, status: 'warning', duration: 3000 });
      return;
    }
    setError('');
    setEnviando(true);
    try {
      const payload = {
        convocatoriaId: Number(convocatoriaId),
        ...form,
        curp: form.curp ? form.curp.trim().toUpperCase() : undefined,
      };
      const { data } = await axiosInstance.post('/deportes/inscripciones', payload);
      setResultado(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e: any) {
      const msg = e?.response?.data?.message || e.message || 'No se pudo registrar la inscripción';
      setError(msg);
      toast({ title: msg, status: 'error', duration: 4000 });
    } finally {
      setEnviando(false);
    }
  };

  const nombreConv = convocatorias.find((c) => String(c.id) === String(convocatoriaId))?.nombre;

  return (
    <Box minH="100vh" bg="gray.50">
      <Box bg="#5b182b" py={8} px={{ base: 4, md: 10 }} color="white" textAlign="center">
        <Flex justify="center" align="center" mb={2}>
          <Box bg="white" borderRadius="full" w={14} h={14} display="flex" alignItems="center" justifyContent="center" mr={4}>
            <Icon as={MdHowToReg} color="#5b182b" boxSize={8} />
          </Box>
          <Box textAlign="left">
            <Heading size="lg">Inscripción Deportiva</Heading>
            <Text fontSize="sm" opacity={0.85}>Dirección de Deportes</Text>
          </Box>
        </Flex>
        <Text fontSize="sm" opacity={0.75} mt={2}>H. Ayuntamiento de Totolac · Tlaxcala</Text>
      </Box>

      <Box py={8} px={{ base: 4, md: 10 }} maxW="800px" mx="auto">
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between" wrap="wrap">
            <Button leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" color="gray.500"
              onClick={() => navigate('/portal/deportes/convocatorias')}>
              Convocatorias
            </Button>
          </HStack>

          {resultado ? (
            <Card shadow="md">
              <CardBody textAlign="center" py={10}>
                <Icon as={CheckCircleIcon} boxSize={14} color="green.400" mb={4} />
                <Heading size="md" color="green.600">¡Inscripción registrada!</Heading>
                <Text color="gray.600" mt={2}>
                  Su solicitud de inscripción fue recibida correctamente.
                </Text>
                <Box mt={5} p={4} bg="gray.50" borderRadius="md" display="inline-block">
                  <Text fontSize="xs" color="gray.500">FOLIO DE INSCRIPCIÓN</Text>
                  <Text fontFamily="mono" fontWeight="bold" fontSize="2xl" color="#5b182b">
                    {resultado.folio || '—'}
                  </Text>
                  {resultado.estatus && (
                    <Badge mt={2} colorScheme="blue">{resultado.estatus}</Badge>
                  )}
                </Box>
                <Text fontSize="sm" color="gray.500" mt={5}>
                  Conserve su folio para dar seguimiento a su inscripción.
                </Text>
                <HStack justify="center" spacing={3} mt={6} wrap="wrap">
                  <Button colorScheme="teal" size="sm" onClick={() => {
                    setResultado(null);
                    setForm({ ...initialForm });
                  }}>Nueva inscripción</Button>
                  <Button variant="outline" size="sm" onClick={() => navigate('/portal/2026')}>
                    Ir al Portal
                  </Button>
                </HStack>
              </CardBody>
            </Card>
          ) : (
            <Card shadow="md">
              <CardHeader bg="gray.50" borderTopRadius="md">
                <HStack>
                  <Icon as={MdSportsSoccer} color="#5b182b" />
                  <Heading size="sm" color="#5b182b">Datos del aspirante</Heading>
                </HStack>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  Complete el formulario para inscribirse. Los campos marcados con * son obligatorios.
                </Text>
              </CardHeader>
              <CardBody>
                <VStack spacing={5} align="stretch">
                  {error && (
                    <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>
                  )}

                  <FormControl isRequired>
                    <FormLabel fontSize="sm">Convocatoria</FormLabel>
                    {loadingConv ? (
                      <HStack><Spinner size="sm" /><Text fontSize="sm" color="gray.500">Cargando convocatorias…</Text></HStack>
                    ) : (
                      <Select placeholder="Seleccione una convocatoria" value={convocatoriaId}
                        onChange={(e) => setConvocatoriaId(e.target.value)}>
                        {convocatorias.map((c) => (
                          <option key={c.id} value={c.id}>{c.nombre}</option>
                        ))}
                      </Select>
                    )}
                    {!loadingConv && convocatorias.length === 0 && (
                      <Text fontSize="xs" color="orange.500" mt={1}>
                        No hay convocatorias vigentes por el momento.
                      </Text>
                    )}
                    {nombreConv && (
                      <Text fontSize="xs" color="gray.400" mt={1}>Convocatoria: {nombreConv}</Text>
                    )}
                  </FormControl>

                  <Divider />

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Nombre(s)</FormLabel>
                      <Input value={form.nombre} onChange={(e) => setField('nombre', e.target.value)} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Apellido paterno</FormLabel>
                      <Input value={form.apPaterno} onChange={(e) => setField('apPaterno', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Apellido materno</FormLabel>
                      <Input value={form.apMaterno} onChange={(e) => setField('apMaterno', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm">Fecha de nacimiento</FormLabel>
                      <Input type="date" value={form.fechaNacimiento}
                        onChange={(e) => setField('fechaNacimiento', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Sexo</FormLabel>
                      <Select placeholder="Seleccione" value={form.sexo}
                        onChange={(e) => setField('sexo', e.target.value)}>
                        <option value="Mujer">Mujer</option>
                        <option value="Hombre">Hombre</option>
                        <option value="Otro">Otro</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">CURP</FormLabel>
                      <Input value={form.curp} textTransform="uppercase" maxLength={18}
                        onChange={(e) => setField('curp', e.target.value.toUpperCase())} />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Teléfono</FormLabel>
                      <Input value={form.telefono} onChange={(e) => setField('telefono', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Correo electrónico</FormLabel>
                      <Input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <Divider />
                  <Text fontSize="sm" fontWeight="bold" color="gray.600">Domicilio</Text>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Localidad</FormLabel>
                      <Input value={form.localidad} onChange={(e) => setField('localidad', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Colonia</FormLabel>
                      <Input value={form.colonia} onChange={(e) => setField('colonia', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">Calle / vialidad</FormLabel>
                      <Input value={form.vialidad} onChange={(e) => setField('vialidad', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Número exterior</FormLabel>
                      <Input value={form.numeroExterior} onChange={(e) => setField('numeroExterior', e.target.value)} />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Referencias</FormLabel>
                      <Input value={form.referencias} onChange={(e) => setField('referencias', e.target.value)} />
                    </FormControl>
                  </SimpleGrid>

                  <Button colorScheme="teal" size="lg" onClick={handleSubmit} isLoading={enviando}
                    loadingText="Enviando…" leftIcon={<Icon as={MdHowToReg} />}>
                    Enviar inscripción
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          )}

          <Text fontSize="xs" color="gray.400" textAlign="center">
            Dirección de Deportes · H. Ayuntamiento de Totolac · Tlaxcala
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default InscripcionDeportesPublico;
